import React, { useState, useEffect, useRef } from 'react';
import {
  bagTakeAwayPriceFor,
  sendsTheaterPerformance,
  theaterOrderFields,
} from '../../utils/orderTypes';
import axios from 'axios';
import { cloneDeep, isEmpty } from 'lodash';
import moment from 'moment';
import configData from '../../config.json';
import { Modal } from '../../components/ui/Modal';
import Loader from '../../components/Loader';
import './Payment.css';
import { useDataContext } from '../../context/data.js';
import translations from '../../constants/translations.json';
import LoyaltyBanner from './LoyaltyBanner';
import { useStampWallet } from '../../context/stampWallet';
import {
  isWoltQuoteFresh,
  woltFeeChanged,
  woltOrderFields,
  woltQuoteFee,
} from '../../utils/woltDelivery';
import { discountMessage } from './DiscountCodeBlock';
import {
  guestFacingError,
  serverMessage,
  soldOutFromError,
  subscriptionBlockedFromError,
} from '../../utils/serverError';

const Payment = ({
  orderItems,
  setOrderItems,
  totalPrice,
  currency = 'DKK',
  restaurant,
  userProfile,
  onPayment,
  myCards,
  setMyCards,
  onCapacityFull,
  addNewCard,
  deleteCardConfirmation,
  onPersistOrderData,
  resetOrderInfo,
  formatOrdersForCheckout,
  isDineIn,
  tableNumbers,
  tableNumber,
  setTableNumber,
  tipAmount,
  bagFee,
  deliveryFee,
  hasBag,
  hasDeliveryFee,
  orderType,
  woltDelivery,
  // What the guest was shown for the quote above, and when it arrived — Pay
  // re-quotes a lapsed one and stops if the price moved (see handlePayNow).
  woltDeliveryFee = '',
  woltQuoteFetchedAt = null,
  // The page's verdict on the delivery minimum for the cart as it stands.
  woltMinOrder,
  // Fetches a fresh quote (resolving to it, or null after telling the guest why).
  onRequireWoltQuote,
  deliveryAddress,
  tipId,
  comments,
  setComments,
  deliveryComment,
  phoneNumber,
  isGuest,
  guestId,
  pickup,
  pickupDate,
  pickupTime,
  roomNumber,
  currentStep,
  navigateToStep,
  tipPercentage,
  setTipPercentage,
  onCheckMinimumAmountValidation,
  fromKiosk = false,
  loyaltyEmail,
  loyaltyPhone,
  onLoyaltyDiscount,
  // Guest discount code: the CODE STRING only. The server re-validates it,
  // computes the amount and reserves the redemption; nothing about the money is
  // ever sent from here. `discountAmount` is display/dependency-tracking only.
  discountCode,
  discountAmount = 0,
  onDiscountCodeRejected,
  // Theater mode: the id of the chosen performance ("YYYY-MM-DD#idx"). The
  // server validates it and derives all pickup timing itself; the key must be
  // absent from the payload whenever theater mode is off or nothing is chosen.
  theaterPerformanceId = null,
  // The guest is on the pre-order flow, not merely at a venue that has one.
  isTheaterPreorder = false,
  // The other theater flow: an immediate order that names a performance purely
  // as context for the kitchen, in place of a table number.
  isTheaterOrderNow = false,
  onTheaterPerformanceRejected,
  // Ordering paused (403 subscription_blocked): the page shows the message in
  // its error modal.
  onOrderingPaused,
}) => {
  const { language } = useDataContext();

  // In-cart voucher redemption (§9.3). useStampWallet() returns undefined when no
  // StampWalletWrapper is mounted; coalesce so checkout is unaffected when absent.
  const wallet = useStampWallet() || {};
  const { stagedVoucher, clearStagedVoucher, refreshAfterOrder, voucherDiscount = 0 } = wallet;

  // Server-constructed reward line: the client sends ONLY the staged choice
  // (voucherCode + rewardProductId); never isReward/price (A6 / §7.4 / §11 C4).
  // undefined when nothing is staged so the payload key is simply absent. The
  // context's stagedVoucher shape is { voucher, rewardProductId } (§9.1).
  const stagedVoucherCode =
    stagedVoucher?.voucher?.Code || stagedVoucher?.voucher?.code || stagedVoucher?.voucherCode;
  const redeemVoucher =
    stagedVoucherCode && stagedVoucher?.rewardProductId
      ? {
          voucherCode: stagedVoucherCode,
          rewardProductId: stagedVoucher.rewardProductId,
        }
      : undefined;

  // v1 = one voucher per order: clear the staged voucher after a successful
  // submit and refresh the wallet (the server has reserved it on the order).
  const onOrderSubmitted = () => {
    if (typeof clearStagedVoucher === 'function') clearStagedVoucher();
    if (typeof refreshAfterOrder === 'function') refreshAfterOrder(restaurant?._id);
  };

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const fetchSavedCards = async () => {
      try {
        const response = await axios({
          method: 'GET',
          url: `${configData.SERVER_URL}/orders/v2/pmlists?restaurant=${restaurant?._id}`,
          headers: {
            Authorization: `Bearer ${userProfile?.token}`,
          },
        });
        if (isMounted) {
          setMyCards(response?.data?.cards || []);
        }
      } catch (error) {
        if (isMounted) {
          console.error('Error fetching saved cards:', error);
        }
      }
    };

    if (userProfile?.token) {
      fetchSavedCards();
    }

    return () => {
      isMounted = false;
    };
  }, [userProfile, restaurant?._id]);

  const handlePayLater = () => {
    onPayment('parktable');
  };

  // The delivery half of paying: the cart must still clear the venue's minimum
  // (the quantity buttons stay live after the review-step check), and the
  // quote must be one Wolt still honours. A lapsed or missing one is fetched
  // again; if its price differs from what the guest was shown, the new total
  // is on screen by the time this returns and the guest presses Pay again —
  // they are never charged a fee they did not see.
  const isWoltOrder = orderType === 'delivery' && Boolean(restaurant?.EnableWoltDelivery);

  // Resolves to the fresh shipment to book, or null when payment must stop.
  const requoteWoltDelivery = async () => {
    if (typeof onRequireWoltQuote !== 'function') return null;

    setLoading(true);
    const fresh = await onRequireWoltQuote();
    setLoading(false);
    if (!fresh) return null;

    const freshFee = woltQuoteFee(fresh);
    if (woltFeeChanged(woltDeliveryFee, freshFee)) {
      alert(
        discountMessage(
          language,
          'wolt_quote_price_changed',
          { amount: freshFee, currency },
          `The delivery price has been updated to ${freshFee} ${currency}. Please check the new total and pay again.`,
        ),
      );
      return null;
    }

    return fresh;
  };

  const handlePayNow = async (method) => {
    if (onCheckMinimumAmountValidation && onCheckMinimumAmountValidation()) return;

    let woltShipment = woltDelivery;
    if (isWoltOrder) {
      if (woltMinOrder && !woltMinOrder.met) {
        alert(woltMinOrder.message);
        return;
      }

      if (!isWoltQuoteFresh(woltDelivery, woltQuoteFetchedAt)) {
        woltShipment = await requoteWoltDelivery();
        if (!woltShipment) return;
      }
    }

    // The Wolt keys, built once for both branches below from the shipment just
    // confirmed — never from a prop that may still hold the lapsed quote.
    const woltFields = woltOrderFields({
      orderType,
      restaurant,
      woltDelivery: woltShipment,
      deliveryComment,
      deliveryAddress,
    });

    setLoading(true);

    // Persist current order state
    onPersistOrderData(guestId);

    try {
      const paymentMethodToUse = method;
      let root_url = window.location.origin
        ? window.location.origin + '/'
        : window.location.protocol + '/' + window.location.host + '/';
      const isAdvanceOrder = !isDineIn && pickup === 'later';
      const currentDate = moment(pickupDate).format('MM/DD/YYYY');
      const currentTime = moment(pickupTime, 'HH:mm').format('h:mm A');
      const formattedDateTime = moment(currentDate + ' ' + currentTime).format();

      // If using mobile payment methods, handle differently
      if (['mobile_pay', 'apple_pay', 'google_pay', 'vipps'].includes(paymentMethodToUse)) {
        // Format orders for the API
        const formattedOrders = formatOrdersForCheckout(orderItems);
        const isMobilePay = paymentMethodToUse === 'mobile_pay';
        const isVipps = paymentMethodToUse === 'vipps';

        // Prepare the order payload
        const orderPayload = {
          restaurantId: restaurant._id,
          date: new Date(),
          isDineIn: isDineIn,
          mobilenumber: phoneNumber,
          notes: comments,
          tableNumber: tableNumber ? tableNumber?.value : '',
          promoCodeId: '',
          // Guest discount code. A dedicated top-level key — never overloaded
          // onto promoCodeId, which is an unvalidated client string written
          // straight through. Omitted when no code is applied, so an API that
          // does not know the field sees exactly the payload it sees today.
          discountCode: discountCode || undefined,
          // In-cart reward redemption (§9.3). Dedicated key — never overloads
          // promoCodeId. Server validates/reserves + constructs the reward line.
          redeemVoucher,
          payment_method: paymentMethodToUse,
          isMobilepay: isMobilePay,
          isVipps: isVipps,
          tipId: tipId || [],
          isweb: true,
          iskiosk: fromKiosk || false,
          isAdvanceOrder: isAdvanceOrder,
          advanceOrderDate: isAdvanceOrder ? formattedDateTime : undefined,
          // Theater mode — the two keys, decided in one place shared with the
          // page (utils/orderTypes.theaterOrderFields).
          ...theaterOrderFields({
            isTheaterPreorder,
            isTheaterOrderNow,
            isDineIn,
            theaterPerformanceId,
          }),
          successuri: `${root_url}api/redirect${
            restaurant.AutoAccept
              ? `?showAcceptModal=1&clearCart=1&return_url=${window.location.origin}${window.location.pathname}`
              : `?clearCart=1${fromKiosk ? '&fromKiosk=1' : ''}&return_url=${
                  window.location.origin
                }${window.location.pathname}`
          }`,
          failuri: `${root_url}api/redirect?persistData=1`,
          order: formattedOrders,
          bagtakeawayprice:
            bagTakeAwayPriceFor({ restaurant, orderType, isDineIn, hasBag }),
          deliveryfee:
            restaurant?.HasDeliveryFee && !isDineIn && hasDeliveryFee
              ? restaurant?.DeliveryFee || 0
              : 0,
          takeAwayRoom: restaurant?.AskTakeAwayRoom && !isDineIn ? roomNumber : '',
          isGuest: isGuest,
          guestId: isGuest ? guestId : '',
          // woltShipment / deliveryComment / deliveryAddress / isDelivery —
          // only a delivery order carries a shipment (utils/woltDelivery).
          ...woltFields,
        };

        const response = await axios({
          method: 'POST',
          url: `${configData.SERVER_URL}/order/checkout`,
          data: orderPayload,
          headers: {
            Authorization: `Bearer ${userProfile?.token || ''}`,
          },
        });

        setLoading(false);
        // Clear the cart
        resetOrderInfo();
        onOrderSubmitted();

        // Zero-value order: a 100% discount code leaves nothing to charge, so
        // checkout() places and confirms the order itself and returns
        // { zeroValue, orderId, order_details } instead of a provider URL.
        // Branch on it BEFORE treating the body as a redirect — otherwise this
        // calls window.location.replace(<object>) after the order was placed.
        // The order_details check is not decoration: onPayment(null, undefined)
        // falls past the page's "already placed" branch and past its parktable
        // branch, leaving the page on a loader it never clears. Returning is the
        // honest thing to do — the order is placed and the cart is already gone.
        if (response.data?.zeroValue) {
          if (response.data.order_details) {
            onPayment(null, response.data.order_details);
          }
          return;
        }

        // Redirect to payment provider
        window.location.replace(response.data);
      } else {
        // Handle card payments and other payment methods
        const formattedOrders = formatOrdersForCheckout(orderItems);

        const orderPayload = {
          restaurantId: restaurant._id,
          date: new Date(),
          isDineIn: isDineIn,
          mobilenumber: phoneNumber,
          notes: comments,
          tableNumber: tableNumber ? tableNumber?.value : '',
          promoCodeId: '',
          // Guest discount code — the code string only, dedicated key.
          discountCode: discountCode || undefined,
          // In-cart reward redemption (§9.3). Dedicated key — never overloads
          // promoCodeId. Server validates/reserves + constructs the reward line.
          redeemVoucher,
          payment_method: paymentMethodToUse,
          isMobilepay: false,
          tipId: tipId || [],
          isweb: true,
          iskiosk: fromKiosk || false,
          isAdvanceOrder: isAdvanceOrder,
          advanceOrderDate: isAdvanceOrder ? formattedDateTime : undefined,
          // Theater mode — same rule as the wallet branch above.
          ...theaterOrderFields({
            isTheaterPreorder,
            isTheaterOrderNow,
            isDineIn,
            theaterPerformanceId,
          }),
          successuri: `${root_url}api/redirect${
            restaurant.AutoAccept
              ? `?showAcceptModal=1&clearCart=1&return_url=${window.location.origin}${window.location.pathname}`
              : `?clearCart=1${fromKiosk ? '&fromKiosk=1' : ''}&return_url=${
                  window.location.origin
                }${window.location.pathname}`
          }`,
          failuri: `${root_url}api/redirect?persistData=1`,
          order: formattedOrders,
          bagtakeawayprice:
            bagTakeAwayPriceFor({ restaurant, orderType, isDineIn, hasBag }),
          deliveryfee:
            restaurant?.HasDeliveryFee && !isDineIn && hasDeliveryFee
              ? restaurant?.DeliveryFee || 0
              : 0,
          isParkTable: paymentMethodToUse === 'parktable',
          takeAwayRoom: restaurant?.AskTakeAwayRoom && !isDineIn ? roomNumber : '',
          isGuest: isGuest,
          guestId: isGuest ? guestId : '',
          // woltShipment / deliveryComment / deliveryAddress / isDelivery —
          // only a delivery order carries a shipment (utils/woltDelivery).
          ...woltFields,
          source: 'web',
        };

        // console.log('orderPayload', orderPayload);

        let url = `${configData.SERVER_URL}/orders/v2/preorder`;

        if (isGuest) {
          url = `${configData.SERVER_URL}/order/checkout`;
        }

        const response = await axios({
          method: 'POST',
          url,
          data: orderPayload,
          headers: {
            Authorization: `Bearer ${userProfile?.token || ''}`,
          },
        });

        setLoading(false);
        // Clear the cart
        resetOrderInfo();
        onOrderSubmitted();

        // Handle the response - save order details and notify the user
        if (response.data) {
          // Same zero-value case as the mobile-pay branch above. This is the
          // branch the zero-total path actually lands in.
          if (response.data?.zeroValue) {
            if (response.data.order_details) {
              onPayment(null, response.data.order_details);
            }
            return;
          }

          if (response.data?.order_details) {
            // Save order info to local storage for tracking
            localStorage.setItem('hw_pending_orderv2', JSON.stringify(response.data.order_details));

            // Call the onPayment callback to handle showing the confirmation modal
            onPayment(paymentMethodToUse, response.data.order_details);

            return;
          }

          window.location.replace(response.data);
        }
      }
    } catch (error) {
      setLoading(false);

      // Ordering paused after the page loaded (the venue's subscription was
      // blocked): hand the server's reason to the page's error modal.
      if (subscriptionBlockedFromError(error) && typeof onOrderingPaused === 'function') {
        onOrderingPaused(serverMessage(error));
        return;
      }

      // The order could not be placed with this discount code (it ran out, the
      // guest already used it, the window closed, …). Checked BEFORE the 429
      // branch on purpose: `rate_limited` is also a 429, and treating it as a
      // capacity problem would open the wrong modal. The contract is: clear the
      // code, show the localized reason, put the guest back on the review step.
      // Never silently retry, never auto-resubmit a payment — the guest presses
      // Pay again and the order goes through at full price.
      const discountCodeError = error?.response?.data?.discountCodeError;
      if (discountCodeError) {
        if (typeof onDiscountCodeRejected === 'function') {
          onDiscountCodeRejected(discountCodeError, error?.response?.data?.Message);
        }
        return;
      }

      // Sold out — checked BEFORE the theater branch on purpose. Both are
      // 400/409 with a Message, so at a theater venue a sold-out drink used to
      // be reported as a rejected performance: the guest's chosen show was
      // cleared and they were sent back to pick another one, for a cola.
      //
      // The backend has already worded this in the guest's language and knows
      // the difference between "none left" and "fewer left than you asked for",
      // so show it as it stands and leave the basket alone — the guest has to be
      // able to change the quantity of the line that is short.
      const soldOut = soldOutFromError(error);
      if (soldOut) {
        alert(guestFacingError(error));
        return;
      }

      // Theater mode: the chosen performance was refused (expired, curtain
      // passed, unknown id — HTTP 400/409 with { Message }). Only checked when
      // an id was actually sent AND the response carries the gate's capital-M
      // Message field — settlement failures use lowercase `message` and must
      // fall through to the generic handling below. The page handler shows the
      // localized reason and puts the guest back on review to pick another
      // performance. Never auto-retry a payment.
      if (
        sendsTheaterPerformance({ isTheaterPreorder, isTheaterOrderNow, isDineIn }) &&
        theaterPerformanceId &&
        [400, 409].includes(error?.response?.status) &&
        error?.response?.data?.Message &&
        typeof onTheaterPerformanceRejected === 'function'
      ) {
        onTheaterPerformanceRejected(error?.response?.data?.Message);
        return;
      }

      // Theater PRE-ORDER: a 429 would mean the chosen performance is full.
      // `nextAvailableSlot` is a clock time the guest cannot act on — the
      // pre-order has no pickup time picker — so hand it to the same page
      // handler as the 400/409 gate, with a localized reason. Only the
      // pre-order: an order for now at a theater is capacity-gated like any
      // walk-in, so its 429 means the venue is busy and belongs to the generic
      // modal below. Checked BEFORE it.
      if (
        isTheaterPreorder &&
        !isDineIn &&
        theaterPerformanceId &&
        error?.response?.status === 429 &&
        typeof onTheaterPerformanceRejected === 'function'
      ) {
        onTheaterPerformanceRejected(translations[language]?.order_information_theater_full);
        return;
      }

      if (error?.response?.status === 429) {
        if (typeof onCapacityFull === 'function') {
          onCapacityFull(error?.response?.data?.nextAvailableSlot || null);
        }
        return;
      }
      console.error('Payment error:', error);
      console.error(error);
      // guestFacingError reads error.response.data, not the AxiosError itself.
      // The old expression read error.Message (undefined) then error.message
      // (axios's own "Request failed with status code 409"), so the guest never
      // saw the venue-is-closed or menu-hours explanation either.
      alert(guestFacingError(error));
    }
  };

  // Zero-total order (e.g. a stampcard reward made the cart free): there is
  // nothing to pay, so skip the payment-method step and place the order directly
  // (payment_method=null → "no online payment"). Runs once; redeemVoucher rides
  // along in the payload so the server reserves/burns the voucher.
  const zeroTotalPlacedRef = useRef(false);
  useEffect(() => {
    const staged = Number(voucherDiscount) || 0;
    const effectiveTotal = Number(totalPrice || 0) - staged;
    if (
      !zeroTotalPlacedRef.current &&
      !loading &&
      // Only the reward path auto-submits. A cart that is already free for some
      // other reason (a 100% discount code) keeps production behaviour: the guest
      // picks a method and the server answers zeroValue.
      staged > 0 &&
      effectiveTotal <= 0 &&
      Array.isArray(orderItems) &&
      orderItems.length > 0
    ) {
      zeroTotalPlacedRef.current = true;
      handlePayNow(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalPrice, voucherDiscount, orderItems]);

  const renderCardType = (card_type) => {
    if (!card_type) return null;

    if (card_type.toUpperCase() === 'VISA') {
      return translations[language]?.payment_card_button_visa;
    } else if (card_type.toUpperCase() === 'MC') {
      return translations[language]?.payment_card_button_mastercard;
    } else if (card_type.toUpperCase() === 'AMEX') {
      return translations[language]?.payment_card_button_amex;
    } else {
      return translations[language]?.payment_card_button_unknown;
    }
  };

  const renderCardExpiryDate = (card_exp_date) => {
    if (!card_exp_date) return null;

    return card_exp_date?.replaceAll('-', '/');
  };

  // Check if the table is a ParkTable (for "Pay later" option)
  const isParkTable =
    restaurant?._id === '69bd59b55a10b5001b234d1d'
      ? restaurant?.HasParkTable
      : isDineIn &&
        tableNumber &&
        restaurant?.HasParkTable &&
        ((Array.isArray(tableNumbers) &&
          tableNumbers.find((t) => t.number === tableNumber?.value)?.isParkTable) ||
          tableNumber?.isParkTable === true);

  const hideOtherPayments =
    restaurant?._id === '6797598891cb2a001995eeb5' ||
    restaurant?._id === '67bdcd9ab0d6ca001912ec7a' ||
    restaurant?._id === '682d79a98f4810001ac860e1' ||
    restaurant?._id === '68e672117bcfc90019ce1462' ||
    restaurant?._id === '6852ac0f9a03120019b3fedc';

  return (
    <>
      {loading && <Loader />}
      {/* Loyalty Banner */}
      <LoyaltyBanner
        restaurant={restaurant}
        userProfile={userProfile}
        email={loyaltyEmail}
        phone={loyaltyPhone}
        onDiscountApplied={(discountPercent) => {
          // Discount is applied server-side during checkout; bubble up for the footer total.
          if (onLoyaltyDiscount) onLoyaltyDiscount(discountPercent);
        }}
      />

      <div className="payment-container space-y-[16px] lg:space-y-[75px]">
        {/* Pay Later Section */}
        {isParkTable && (
          <div className="restaurant-menu-section rounded-xl">
            <div className="!px-3 !py-[20px] lg:!px-4 lg:!py-[27px] border-b border-[#f5f5f5]">
              <div className="flex items-center">
                <img src="/receipt_icon.png" alt="receipt_icon" className="mr-2 lg:mr-3" />
                <p className="text-base sm:text-lg md:text-xl font-bold">
                  {restaurant?._id === '682d79a98f4810001ac860e1'
                    ? 'Sæt på bordets regning og betal senere'
                    : translations[language]?.payment_park_table_title}
                </p>
              </div>
            </div>
            <div className="!px-3 !py-4 lg:!px-4 lg:!py-8 flex flex-col sm:flex-row items-start sm:items-center justify-between">
              <div className="flex-1 mb-2 sm:!mb-0">
                <label className="block text-sm font-bold">
                  {translations[language]?.payment_park_table_label}
                </label>
                <p className="text-[11px] text-[#707070]">
                  {translations[language]?.payment_park_table_description}
                </p>
              </div>
              <div className="w-full sm:w-auto flex-1">
                <button
                  className="text-sm w-full h-[50px] bg-primary hover:bg-secondary text-white font-bold px-8 py-[13px] rounded-full transition-colors duration-300"
                  onClick={handlePayLater}
                >
                  {restaurant?._id === '682d79a98f4810001ac860e1'
                    ? translations[language]?.order_now
                    : translations[language]?.payment_park_table_button}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Pay Now Section */}
        <>
          {isGuest && !hideOtherPayments && !fromKiosk ? (
            <>
              {isParkTable ? null : (
                <div className="restaurant-menu-section rounded-xl">
                  <div className="!px-3 !py-[20px] lg:!px-4 lg:!py-[27px] border-b border-[#f5f5f5]">
                    <div className="flex items-center">
                      <img src="/smartphone_icon.png" alt="phone_icon" className="mr-2 lg:mr-3" />
                      <p className="text-base sm:text-lg md:text-xl font-bold">
                        {translations[language]?.payment_guest_title}
                      </p>
                    </div>
                  </div>

                  <div className="space-y-8">
                    {/* Pay now as Guest Button */}
                    <div className="!px-3 !py-4 lg:!px-4 lg:!py-8 flex flex-col sm:flex-row items-start sm:items-center justify-between">
                      <div className="flex-1 mb-2 sm:!mb-0">
                        <label className="block text-sm font-bold">
                          {translations[language]?.payment_guest_label}
                        </label>
                        <p className="text-[11px] text-[#707070]">
                          {translations[language]?.payment_guest_description}
                        </p>
                      </div>
                      <div className="w-full sm:w-auto flex-1">
                        <button
                          className={`text-sm w-full h-[50px] bg-[#222] hover:bg-gray-700 transition-colors text-white font-bold px-4 py-[13px] rounded-full flex items-center justify-center`}
                          onClick={() => handlePayNow(null)}
                          disabled={loading}
                        >
                          {translations[language]?.payment_guest_button}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <>
              {isParkTable ? null : (
                <>
                  {/* Mobile Payment */}
                  <div className="restaurant-menu-section rounded-xl" hidden={hideOtherPayments}>
                    <div className="!px-3 !py-[20px] lg:!px-4 lg:!py-[27px] border-b border-[#f5f5f5]">
                      <div className="flex items-center">
                        <img src="/smartphone_icon.png" alt="phone_icon" className="mr-2 lg:mr-3" />
                        <p className="text-base sm:text-lg md:text-xl font-bold">
                          {translations[language]?.payment_mobile_title}
                        </p>
                      </div>
                    </div>

                    <div className="space-y-8">
                      {/* MobilePay Button */}
                      {restaurant?.isMobilePay && restaurant?.currency?.code !== 'NOK' && (
                        <div className="!px-3 !py-4 lg:!px-4 lg:!py-8 border-b border-[#f5f5f5] flex flex-col sm:flex-row items-start sm:items-center justify-between">
                          <div className="flex-1 mb-2 sm:!mb-0">
                            <label className="block text-sm font-bold">
                              {translations[language]?.payment_mobile_pay_label}
                            </label>
                            <p className="text-[11px] text-[#707070]">
                              {translations[language]?.payment_mobile_pay_description}
                            </p>
                          </div>
                          <div className="w-full sm:w-auto flex-1">
                            <button
                              className={`text-sm w-full h-[50px] bg-[#5A78FF] hover:bg-[#4A6AD2] transition-colors text-white font-bold px-6 py-[5px] rounded-full flex items-center justify-center`}
                              onClick={() => handlePayNow('mobile_pay')}
                              disabled={loading}
                            >
                              {translations[language]?.payment_mobile_pay_button}
                              <span className="ml-4">
                                <img
                                  src="/mobilepay40x40.png"
                                  alt={translations[language]?.payment_mobile_pay_button}
                                  className="h-10 invert brightness-0"
                                />
                              </span>
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Vipps Button */}
                      {restaurant?.isVipps && (
                        <div className="!px-3 !py-4 lg:!px-4 lg:!py-8 border-b border-[#f5f5f5] flex flex-col sm:flex-row items-start sm:items-center justify-between">
                          <div className="flex-1 mb-2 sm:!mb-0">
                            <label className="block text-sm font-bold">
                              {translations[language]?.payment_vipps_pay_label}
                            </label>
                            <p className="text-[11px] text-[#707070]">
                              {translations[language]?.payment_vipps_pay_description}
                            </p>
                          </div>
                          <div className="w-full sm:w-auto flex-1">
                            <button
                              className={`text-sm w-full h-[50px] bg-[#FF725E] hover:bg-[#FC5740] transition-colors text-white font-bold px-6 py-[13px] rounded-full flex items-center justify-center`}
                              onClick={() => handlePayNow('vipps')}
                              disabled={loading}
                            >
                              {translations[language]?.payment_vipps_pay_button}
                              <span className="ml-4">
                                <img
                                  src="/vipps.png"
                                  alt={translations[language]?.payment_vipps_pay_button}
                                />
                              </span>
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Apple Pay Button */}
                      <div className="!px-3 !py-4 lg:!px-4 lg:!py-8 border-b border-[#f5f5f5] flex flex-col sm:flex-row items-start sm:items-center justify-between">
                        <div className="flex-1 mb-2 sm:!mb-0">
                          <label className="block text-sm font-bold">
                            {translations[language]?.payment_apple_pay_label}
                          </label>
                          <p className="text-[11px] text-[#707070]">
                            {translations[language]?.payment_apple_pay_description}
                          </p>
                        </div>
                        <div className="w-full sm:w-auto flex-1">
                          <button
                            className={`text-sm w-full h-[50px] bg-[#222] hover:bg-gray-700 transition-colors text-white font-bold px-4 py-[13px] rounded-full flex items-center justify-center`}
                            onClick={() => handlePayNow('apple_pay')}
                            disabled={loading}
                          >
                            {translations[language]?.payment_apple_pay_button}
                            <span className="ml-4">
                              <img
                                src="/apple_pay.png"
                                alt={translations[language]?.payment_apple_pay_button}
                                className="h-5 invert brightness-0"
                              />
                            </span>
                          </button>
                        </div>
                      </div>

                      {/* Google Pay Button */}
                      <div className="!px-3 !py-4 lg:!px-4 lg:!py-8 flex flex-col sm:flex-row items-start sm:items-center justify-between">
                        <div className="flex-1 mb-2 sm:!mb-0">
                          <label className="block text-sm font-bold">
                            {translations[language]?.payment_google_pay_label}
                          </label>
                          <p className="text-[11px] text-[#707070]">
                            {translations[language]?.payment_google_pay_description}
                          </p>
                        </div>
                        <div className="w-full sm:w-auto flex-1">
                          <button
                            className={`text-sm w-full h-[50px] bg-[#222] hover:bg-gray-700 transition-colors text-white font-bold px-4 py-[13px] rounded-full flex items-center justify-center`}
                            onClick={() => handlePayNow('google_pay')}
                            disabled={loading}
                          >
                            {translations[language]?.payment_google_pay_button}
                            <span className="ml-4">
                              <img
                                src="/google_pay_white.png"
                                alt={translations[language]?.payment_google_pay_button}
                                className="h-5"
                              />
                            </span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </>
          )}

          {/* Credit/Debit Cards */}
          {!isGuest && !hideOtherPayments && !fromKiosk && (
            <>
              {isParkTable ? null : (
                <div className="restaurant-menu-section rounded-xl">
                  <div className="!px-3 !py-[20px] lg:!px-4 lg:!py-[27px] border-b border-[#f5f5f5]">
                    <div className="flex items-center">
                      <img src="/card_icon.png" alt="card_icon" className="mr-2 lg:mr-3" />
                      <p className="text-base sm:text-lg md:text-xl font-bold">
                        {translations[language]?.payment_card_title}
                      </p>
                    </div>
                  </div>

                  {/* Saved Cards */}
                  {myCards.map((card, index) => (
                    <div
                      key={index}
                      className="px-4 py-2 border-b border-[#f5f5f5] flex flex-col sm:flex-row items-start sm:items-center justify-between"
                    >
                      <div className="flex-none lg:flex-1">
                        <label className="block text-sm font-bold">
                          {translations[language]?.payment_card_label}
                        </label>
                      </div>

                      <div className="relative w-full xl:w-auto max-w-full lg:max-w-[290px] xl:max-w-auto flex-initial xl:flex-1">
                        <button
                          className={`btn-card h-[100px] transition-all w-full text-[#222] font-medium py-3 px-4 rounded-3xl flex items-center`}
                          onClick={() => handlePayNow(card.id)}
                          disabled={loading}
                        >
                          <div className="flex items-center justify-between w-full">
                            <div className="flex flex-col text-left">
                              <span className="font-bold">{renderCardType(card?.card_type)}</span>
                              <span className="text-xs font-bold">
                                {renderCardExpiryDate(card?.exp_date)}
                              </span>
                              <span className="text-xs text-[#707070]">
                                XXXX XXXX XXXX X{card.masked_card.slice(-3)}
                              </span>
                            </div>

                            {card.card_type.toUpperCase() === 'VISA' ? (
                              <img src="/visa_big.png" className="h-[30px]" alt="Visa" />
                            ) : card.card_type.toUpperCase() === 'MC' ? (
                              <img
                                src="/mastercard_big.png"
                                className="h-[50px]"
                                alt="MasterCard"
                              />
                            ) : card.card_type.toUpperCase() === 'AMEX' ? (
                              <img src="/americanexpress_big.png" alt="American Express" />
                            ) : (
                              <img src="/card_icon.png" alt="card_icon" />
                            )}

                            <button
                              className="absolute top-2 right-2 text-red-500 hover:text-red-700 transition-colors"
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteCardConfirmation(card.id);
                              }}
                            >
                              <i className="fa fa-trash" />
                            </button>
                          </div>
                        </button>
                      </div>
                    </div>
                  ))}

                  {/* Add New Card Button */}
                  <div className="!px-3 !py-4 lg:!px-4 lg:!py-8 flex flex-col sm:flex-row items-start sm:items-center justify-between">
                    <div className="flex-1 mb-2 sm:!mb-0">
                      <label className="block text-sm font-bold">
                        {translations[language]?.payment_card_new_label}
                      </label>
                      <p className="text-[11px] text-[#707070]">
                        {translations[language]?.payment_card_new_description}
                      </p>
                    </div>

                    <div className="w-full sm:w-auto flex-1">
                      <button
                        className="text-sm w-full h-[50px] bg-[#222] hover:bg-gray-700 transition-colors text-white font-bold py-3 px-4 rounded-full flex items-center justify-center"
                        onClick={addNewCard}
                        disabled={loading}
                      >
                        {translations[language]?.payment_card_new_button}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </>
      </div>
    </>
  );
};

export default Payment;
